import axios from 'axios';
import { ContactMessage, AdminStats, LoginRequest, LoginResponse, User, CreateUserRequest, UpdateUserRequest, Post, CreatePostRequest, UpdatePostRequest, PostMediaResponse } from './types';

// Use the same origin/host that the admin panel is accessed from
const API_BASE_URL = process.env.REACT_APP_API_URL || `${window.location.protocol}//${window.location.host}`;

const api = axios.create({
  baseURL: API_BASE_URL,
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('adminToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Don't hijack the login endpoint's own 401 (wrong credentials) —
    // let the Login component surface the error to the user instead of
    // forcing a hard reload back to /login.
    const isLoginRequest = error.config?.url?.includes('/api/auth/login');
    if (error.response?.status === 401 && !isLoginRequest) {
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminUser');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Simple SHA-256 implementation for non-secure contexts
async function sha256(message: string): Promise<string> {
  if (window.crypto && window.crypto.subtle) {
    // Use Web Crypto API if available (HTTPS or localhost)
    const encoder = new TextEncoder();
    const data = encoder.encode(message);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  } else {
    // Fallback: simple hash for non-secure contexts
    // This is a basic implementation - in production, use a proper library
    let hash = 0;
    for (let i = 0; i < message.length; i++) {
      const char = message.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    // Pad to look like a hex hash
    const hexHash = Math.abs(hash).toString(16).padStart(64, '0');
    return hexHash;
  }
}

export const authAPI = {
  login: async (credentials: LoginRequest): Promise<LoginResponse> => {
    // Hash password with SHA-256
    const hashedPassword = await sha256(credentials.password);
    
    const response = await api.post('/api/auth/login', {
      email: credentials.email,
      password: hashedPassword,
    });
    return response.data;
  },
};

export const adminAPI = {
  getStats: async (): Promise<AdminStats> => {
    const response = await api.get('/api/admin/stats');
    return response.data;
  },

  getMessages: async (): Promise<ContactMessage[]> => {
    const response = await api.get('/api/admin/messages');
    return response.data;
  },

  markMessageAsRead: async (id: string): Promise<void> => {
    await api.put(`/api/admin/messages/${id}/read`);
  },

  deleteMessage: async (id: string): Promise<void> => {
    await api.delete(`/api/admin/messages/${id}`);
  },

  // User management
  getUsers: async (): Promise<User[]> => {
    const response = await api.get('/api/admin/users');
    return response.data;
  },

  createUser: async (userData: CreateUserRequest): Promise<User> => {
    const response = await api.post('/api/admin/users', userData);
    return response.data.user;
  },

  updateUser: async (id: string, userData: UpdateUserRequest): Promise<User> => {
    const response = await api.put(`/api/admin/users/${id}`, userData);
    return response.data.user;
  },

  deleteUser: async (id: string): Promise<void> => {
    await api.delete(`/api/admin/users/${id}`);
  },

  toggleUserAdmin: async (id: string): Promise<User> => {
    const response = await api.put(`/api/admin/users/${id}/toggle-admin`);
    return response.data.user;
  },

  // Post management
  getPosts: async (): Promise<Post[]> => {
    const response = await api.get('/api/admin/posts');
    return response.data;
  },

  createPost: async (data: CreatePostRequest): Promise<Post> => {
    const response = await api.post('/api/admin/posts', data);
    return response.data;
  },

  updatePost: async (id: string, data: UpdatePostRequest): Promise<Post> => {
    const response = await api.put(`/api/admin/posts/${id}`, data);
    return response.data;
  },

  deletePost: async (id: string): Promise<void> => {
    await api.delete(`/api/admin/posts/${id}`);
  },

  uploadPostMedia: async (file: File): Promise<PostMediaResponse> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/api/admin/posts/upload-media', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
};

export default api;
